import { useForm } from 'react-hook-form'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useAuthStore } from '../../stores/auth'
import type { LoginForm as LoginFormType } from '../../types'

export function LoginForm() {
  const [showPassword, setShowPassword] = useState(false)
  const { login, isLoading, error, clearError, isAuthenticated, checkAuth } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const next = params.get('next')
  const [info, setInfo] = useState<string | null>(null)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)

  useEffect(() => {
    clearError(); // Clear error on mount
    
    // Check authentication status on mount
    const checkAuthStatus = async () => {
      try {
        await checkAuth()
      } finally {
        setIsCheckingAuth(false)
      }
    }
    checkAuthStatus()
  }, [clearError, checkAuth])

  useEffect(() => {
    if (!isCheckingAuth && isAuthenticated) {
      if (next) {
        navigate(next, { replace: true })
      } else {
        navigate('/dashboard', { replace: true })
      }
    }
    if (next && !info) {
      setInfo('Please sign in to accept your invitation.')
    }
  }, [isAuthenticated, navigate, next, info, isCheckingAuth])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormType>({ mode: 'onBlur' })

  const onSubmit = async (data: LoginFormType) => {
    clearError()
    await login(data.email, data.password)
  }

  // Show loading spinner while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="flex min-h-screen bg-gray-50 items-center justify-center w-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen bg-gray-50 items-center justify-center w-full">
      <div className="flex justify-center w-full">
        <div className="card w-full max-w-md p-8">
          <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-2">
            Sign in to your account
          </h2>
          <p className="text-center text-sm text-gray-600 mb-6">
            Or{' '}
            <Link
              to={next ? `/register?next=${encodeURIComponent(next)}` : '/register'}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              create a new account
            </Link>
          </p>
          {info && <div className="mb-4 text-blue-600 text-center text-sm">{info}</div>}
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                Sorry, we couldn't sign you in. Please check your email and password and try again.
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label htmlFor="login-email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  className="input-field mt-1"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="login-password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="relative mt-1">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className="input-field pr-10"
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 6,
                        message: 'Password must be at least 6 characters',
                      },
                    })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <label htmlFor="login-remember-me" className="ml-2 block text-sm text-gray-900">
                  Remember me
                </label>
                <input
                  id="login-remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>
              <div className="text-sm">
                <Link
                  to="/forgot-password"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Forgot your password?
                </Link>
              </div>
            </div>
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full flex justify-center py-2 px-4"
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
} 